import React from "react";

import clsx from "clsx";
import { type DocumentNode } from "graphql";

import {
  useAttributedAtom,
  useCustomerIdAtom,
  useCustomerTagsAtom,
  useDiscountCodesAtom,
  useDiscountValueAtom,
  useNumberOfOrderItemsAtom,
  useNumberOfTouchpointsAtom,
  useOrderIdAtom,
  useOrderNumberAtom,
  useOrderTagsAtom,
  useOrderTypesAtom,
  useOrderValueAtom,
  useProductsPurchasedAtom,
  useRefundValueAtom,
  useShippingValueAtom,
  useSourceNameAtom,
  useSubscriptionsAtom,
  useTaxValueAtom,
} from "@/atoms/order-page-atoms";
import { type Option } from "@/types/react-select";
import { useCachedQuery } from "@components/hooks";
import { type DataTypeProvider } from "@devexpress/dx-react-grid";
import { useNorthbeamQuery } from "@hooks/http";
import { useOrderQueriesLoading } from "@hooks/use-order-page-loading";
import {
  ValueComparisonType,
  type FetchCustomerTagOptions,
  type FetchOrderDiscountCodeOptions,
  type FetchOrderProductOptions,
  type FetchOrderTagOptions,
  type FetchOrderTypeOptions,
  type FetchSourceNameOptions,
} from "@north-beam/nb-client-side";
import { CustomerTypes } from "@north-beam/nb-common";
import { useOrdersPageFilters } from "@pages/orders/use-orders-filters";
import {
  FETCH_CUSTOMER_TAG_OPTIONS,
  FETCH_DISCOUNT_CODES_OPTIONS,
  FETCH_ORDER_PRODUCT_OPTIONS,
  FETCH_ORDER_TAG_OPTIONS,
  FETCH_ORDER_TYPE_OPTIONS,
  FETCH_SOURCE_NAME_OPTIONS,
} from "@pages/settings/queries";
import { SUBSCRIPTION_TYPES } from "@pages/subscriptions-order-tags/subscription-order-tags";
import { MultiSelect, Select } from "@shared/selects";
import { TextInput } from "@shared/text-input";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import { SOURCE_NAME_TAG } from "./constant";
import { TagsSelector } from "./tags-selector";

export const MultiBadgeEditor = ({
  disabled = false,
  isClearable = false,
  isLoading,
  isSearchable = false,
  label,
  onChange,
  options,
  placeholder,
  shouldAnon,
  tabIndex,
  type,
  value,
}: {
  disabled?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  label?: string;
  onChange: (option: Option["value"][] | null) => void;
  options: Option[];
  placeholder?: string;
  shouldAnon: boolean;
  tabIndex?: string | number;
  type: string;
  value: Option["value"][] | null;
}) => {
  const prefs = usePrefs();
  const doAnon = prefs.isDemoMode;

  return (
    <MultiSelect
      isClearable={isClearable}
      isDisabled={disabled}
      isLoading={isLoading}
      isSearchable={isSearchable}
      label={label}
      onChange={onChange}
      placeholder={placeholder}
      tabIndex={tabIndex}
      value={value ?? []}
      options={
        shouldAnon
          ? options.map((o) => {
              const hash = generateAnonBase64Hash({
                value: o.label,
                prefix: type,
                doAnon,
              });
              return {
                label: hash,
                value: hash,
              };
            })
          : options
      }
      className="w-full"
    />
  );
};

export const MultiBadgeSelection = ({
  disabled = false,
  isSearchable = false,
  onChange,
  options,
  shouldAnon,
  value,
  type,
  dropdownClassName,
  hasTooltip = false,
}: {
  disabled?: boolean;
  isSearchable?: boolean;
  shouldAnon: boolean;
  options: Option[];
  value: Option["value"][] | null;
  type: string;
  dropdownClassName?: string;
  onChange: (option: Option["value"][] | null) => void;
  hasTooltip?: boolean;
}) => {
  const prefs = usePrefs();
  const doAnon = prefs.isDemoMode;

  const filtersCount = value?.length || 0;

  return (
    <div className="flex items-center gap-1.5 text-xs font-normal py-1">
      <TagsSelector
        dropdownClassName={clsx(dropdownClassName, "z-[2005]")}
        disabled={disabled}
        isSearchable={isSearchable}
        options={
          shouldAnon
            ? options.map((o) => {
                const labelHash = generateAnonBase64Hash({
                  value: o.label,
                  prefix: type,
                  doAnon,
                });
                const valueHash = generateAnonBase64Hash({
                  value: o.value,
                  prefix: type,
                  doAnon,
                });

                return {
                  label: labelHash,
                  value: valueHash,
                };
              })
            : options
        }
        values={value || []}
        onChange={onChange}
        hasTooltip={hasTooltip}
      />
      <span
        className={clsx({
          "text-gray": filtersCount === 0 || disabled,
          "text-cosmic": filtersCount > 0 && !disabled,
        })}
      >
        ({filtersCount}) selected
      </span>
    </div>
  );
};

function useGetOptions<Query>(query: DocumentNode) {
  const filterOptions = useOrdersPageFilters();

  const { data, loading } = useNorthbeamQuery<
    Query,
    { dateRange: { start: Date; end: Date } }
  >(query, {
    variables: {
      dateRange: filterOptions.dateRange,
    },
  });
  const cachedData = useCachedQuery(data);
  return { loading, data: cachedData };
}

export const ProductEditor = () => {
  const { loading, data } = useGetOptions<FetchOrderProductOptions>(
    FETCH_ORDER_PRODUCT_OPTIONS,
  );
  const [products, setProducts] = useProductsPurchasedAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <MultiBadgeSelection
      dropdownClassName="w-[300px]"
      disabled={loading || orderQueriesLoading}
      isSearchable={true}
      shouldAnon={true}
      type="product"
      options={(data?.me.products || []).map((p) => ({ value: p, label: p }))}
      value={products}
      onChange={setProducts}
      hasTooltip
    />
  );
};

export const OrderTypesEditor = () => {
  const { loading, data } = useGetOptions<FetchOrderTypeOptions>(
    FETCH_ORDER_TYPE_OPTIONS,
  );

  const [filter, setFilter] = useOrderTypesAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <MultiBadgeSelection
      disabled={loading || orderQueriesLoading}
      isSearchable={false}
      shouldAnon={false}
      type="customerTypes"
      options={(data?.me.orderTypes || []).map((i) => ({
        value: CustomerTypes.contains(i) ? CustomerTypes.getLabel(i) : i,
        label: CustomerTypes.contains(i) ? CustomerTypes.getLabel(i) : i,
      }))}
      value={filter || []}
      onChange={setFilter}
    />
  );
};

export const OrderTagsEditor = () => {
  const { loading, data } = useGetOptions<FetchOrderTagOptions>(
    FETCH_ORDER_TAG_OPTIONS,
  );
  const [filter, setFilter] = useOrderTagsAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <MultiBadgeSelection
      dropdownClassName="w-[300px]"
      disabled={loading || orderQueriesLoading}
      isSearchable={true}
      shouldAnon={true}
      type="tag"
      options={(data?.me.orderTags || []).map((i) => ({ value: i, label: i }))}
      value={filter || []}
      onChange={setFilter}
      hasTooltip
    />
  );
};

export const SourceNameEditor = () => {
  const { loading, data } = useGetOptions<FetchSourceNameOptions>(
    FETCH_SOURCE_NAME_OPTIONS,
  );
  const [filter, setFilter] = useSourceNameAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <MultiBadgeSelection
      dropdownClassName="w-[300px]"
      disabled={loading || orderQueriesLoading}
      isSearchable={true}
      shouldAnon={true}
      type="sourceName"
      options={(data?.me.sourceNames || []).map((i) => ({
        value: i,
        label: i.replace(new RegExp(`^${SOURCE_NAME_TAG}`), ""),
      }))}
      value={filter || []}
      onChange={setFilter}
      hasTooltip
    />
  );
};

export const CustomerTagsEditor = () => {
  const { loading, data } = useGetOptions<FetchCustomerTagOptions>(
    FETCH_CUSTOMER_TAG_OPTIONS,
  );
  const [filter, setFilter] = useCustomerTagsAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <MultiBadgeSelection
      dropdownClassName="w-[300px]"
      disabled={loading || orderQueriesLoading}
      isSearchable={true}
      shouldAnon={true}
      type="customerTag"
      options={(data?.me.customerTags || []).map((i) => ({
        value: i,
        label: i,
      }))}
      value={filter || []}
      onChange={setFilter}
      hasTooltip
    />
  );
};

export const SubscriptionsEditor = () => {
  const orderQueriesLoading = useOrderQueriesLoading();
  const [filter, setFilter] = useSubscriptionsAtom();

  return (
    <MultiBadgeSelection
      disabled={orderQueriesLoading}
      dropdownClassName="w-[200px]"
      isSearchable={false}
      shouldAnon={false}
      type="subscription"
      options={SUBSCRIPTION_TYPES}
      value={filter || []}
      onChange={setFilter}
    />
  );
};

export const DiscountCodesEditor = () => {
  const { loading, data } = useGetOptions<FetchOrderDiscountCodeOptions>(
    FETCH_DISCOUNT_CODES_OPTIONS,
  );
  const [filter, setFilter] = useDiscountCodesAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <MultiBadgeSelection
      dropdownClassName="w-[300px]"
      disabled={loading || orderQueriesLoading}
      isSearchable={true}
      shouldAnon={true}
      type="discount"
      options={(data?.me.discountCodes || []).map((i) => ({
        value: i,
        label: i,
      }))}
      value={filter || []}
      onChange={setFilter}
      hasTooltip
    />
  );
};

export const AttributedEditor = (props: DataTypeProvider.ValueEditorProps) => {
  const [filter, setFilter] = useAttributedAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <MultiBadgeSelection
      disabled={orderQueriesLoading}
      isSearchable={false}
      shouldAnon={false}
      type="attributed"
      options={[
        { value: "Yes", label: "Yes" },
        { value: "No", label: "No" },
      ]}
      value={filter || []}
      onChange={setFilter}
    />
  );
};

export const OrderValueEditor = () => {
  const [filter, setFilter] = useOrderValueAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <ComparisonSelector
      disabled={orderQueriesLoading}
      value={filter?.value ? String(filter.value) : ""}
      comparison={filter?.comparisonOperator}
      onChangeComparison={function (
        value: ValueComparisonType | undefined,
      ): void {
        if (!value && !filter?.value) {
          setFilter(null);
        } else {
          setFilter({ ...filter, comparisonOperator: value });
        }
      }}
      onChangeValue={function (value: string): void {
        if (!value && !filter?.comparisonOperator) {
          setFilter(null);
        } else {
          setFilter({ ...filter, value });
        }
      }}
    />
  );
};

export const DiscountValueEditor = () => {
  const [filter, setFilter] = useDiscountValueAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <ComparisonSelector
      disabled={orderQueriesLoading}
      value={filter?.value ? String(filter.value) : ""}
      comparison={filter?.comparisonOperator}
      onChangeComparison={function (
        value: ValueComparisonType | undefined,
      ): void {
        if (!value && !filter?.value) {
          setFilter(null);
        } else {
          setFilter({ ...filter, comparisonOperator: value });
        }
      }}
      onChangeValue={function (value: string): void {
        if (!value && !filter?.comparisonOperator) {
          setFilter(null);
        } else {
          setFilter({ ...filter, value });
        }
      }}
    />
  );
};

export const ShippingValueEditor = () => {
  const [filter, setFilter] = useShippingValueAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <ComparisonSelector
      disabled={orderQueriesLoading}
      value={filter?.value ? String(filter.value) : ""}
      comparison={filter?.comparisonOperator}
      onChangeComparison={function (
        value: ValueComparisonType | undefined,
      ): void {
        if (!value && !filter?.value) {
          setFilter(null);
        } else {
          setFilter({ ...filter, comparisonOperator: value });
        }
      }}
      onChangeValue={function (value: string): void {
        if (!value && !filter?.comparisonOperator) {
          setFilter(null);
        } else {
          setFilter({ ...filter, value });
        }
      }}
    />
  );
};

export const TaxValueEditor = () => {
  const [filter, setFilter] = useTaxValueAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <ComparisonSelector
      disabled={orderQueriesLoading}
      value={filter?.value ? String(filter.value) : ""}
      comparison={filter?.comparisonOperator}
      onChangeComparison={function (
        value: ValueComparisonType | undefined,
      ): void {
        if (!value && !filter?.value) {
          setFilter(null);
        } else {
          setFilter({ ...filter, comparisonOperator: value });
        }
      }}
      onChangeValue={function (value: string): void {
        if (!value && !filter?.comparisonOperator) {
          setFilter(null);
        } else {
          setFilter({ ...filter, value });
        }
      }}
    />
  );
};

export const RefundValueEditor = () => {
  const [filter, setFilter] = useRefundValueAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <ComparisonSelector
      disabled={orderQueriesLoading}
      value={filter?.value ? String(filter.value) : ""}
      comparison={filter?.comparisonOperator}
      onChangeComparison={function (
        value: ValueComparisonType | undefined,
      ): void {
        if (!value && !filter?.value) {
          setFilter(null);
        } else {
          setFilter({ ...filter, comparisonOperator: value });
        }
      }}
      onChangeValue={function (value: string): void {
        if (!value && !filter?.comparisonOperator) {
          setFilter(null);
        } else {
          setFilter({ ...filter, value });
        }
      }}
    />
  );
};

const getNumberValue = (prevValue: number | undefined, nextValue: string) => {
  const numberedNewVal = Number(nextValue);
  if (nextValue.trim() === "") {
    return undefined;
  }
  if (isNaN(numberedNewVal)) {
    return prevValue;
  }
  return numberedNewVal;
};

export const COMPARISON_OPERATORS_MAPPING = {
  lessThanOrEqual: "<=",
  lessThan: "<",
  equalTo: "=",
  greaterThan: ">",
  greaterThanOrEqualTo: ">=",
};

export type ComparisonOption = {
  value: ValueComparisonType;
  label: string;
};

const ComparisonSelector = ({
  value,
  comparison,
  onChangeComparison,
  onChangeValue,
  disabled,
}: {
  disabled: boolean;
  value: string;
  comparison: ValueComparisonType | undefined;
  onChangeComparison: (value: ValueComparisonType | undefined) => void;
  onChangeValue: (value: string) => void;
}) => {
  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangeValue(event.target.value);
  };

  const comparisonOptions: ComparisonOption[] = [
    { value: ValueComparisonType.equalTo, label: "=" },
    { value: ValueComparisonType.greaterThan, label: ">" },
    { value: ValueComparisonType.greaterThanOrEqualTo, label: ">=" },
    { value: ValueComparisonType.lessThan, label: "<" },
    { value: ValueComparisonType.lessThanOrEqual, label: "<=" },
  ];

  return (
    <div className="flex space-x-0.5">
      <TextInput
        id="comparison-input"
        placeholder="Filter value..."
        className="flex-grow"
        disabled={disabled}
        value={value}
        onChange={handleValueChange}
      />
      <Select
        width="46px"
        isDisabled={disabled}
        placeholder={
          <i className="fa-regular fa-sharp fa-chevron-down text-interstellar" />
        }
        value={comparison}
        onChange={onChangeComparison}
        options={comparisonOptions}
        hideCaret
      />
    </div>
  );
};

export const OrderTouchpointsEditor = () => {
  const [filter, setFilter] = useNumberOfTouchpointsAtom();

  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <ComparisonSelector
      disabled={orderQueriesLoading}
      value={filter?.value ? String(filter.value) : ""}
      comparison={filter?.comparisonOperator}
      onChangeComparison={function (
        value: ValueComparisonType | undefined,
      ): void {
        if (!value && !filter?.value) {
          setFilter(null);
        } else {
          setFilter({ ...filter, comparisonOperator: value });
        }
      }}
      onChangeValue={function (value: string): void {
        const newVal = getNumberValue(filter?.value, value);
        if (!newVal && !filter?.comparisonOperator) {
          setFilter(null);
        } else {
          setFilter({ ...filter, value: newVal });
        }
      }}
    />
  );
};

export const OrderNumberOfOriginalItemsEditor = () => {
  const [filter, setFilter] = useNumberOfOrderItemsAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <ComparisonSelector
      disabled={orderQueriesLoading}
      value={filter?.value ? String(filter.value) : ""}
      comparison={filter?.comparisonOperator}
      onChangeComparison={function (
        value: ValueComparisonType | undefined,
      ): void {
        if (!value && !filter?.value) {
          setFilter(null);
        } else {
          setFilter({ ...filter, comparisonOperator: value });
        }
      }}
      onChangeValue={function (value: string): void {
        const newVal = getNumberValue(filter?.value, value);
        if (!newVal && !filter?.comparisonOperator) {
          setFilter(null);
        } else {
          setFilter({ ...filter, value: newVal });
        }
      }}
    />
  );
};

export const OrderIdEditor = () => {
  const [filter, setFilter] = useOrderIdAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <TextInput
      id="orderId"
      placeholder="Filter value..."
      value={filter || ""}
      onChange={(e) => setFilter(e.currentTarget.value)}
      disabled={orderQueriesLoading}
    />
  );
};

export const OrderNumberEditor = () => {
  const [filter, setFilter] = useOrderNumberAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <TextInput
      id="orderNumber"
      placeholder="Filter value..."
      value={filter || ""}
      onChange={(e) => setFilter(e.currentTarget.value)}
      disabled={orderQueriesLoading}
    />
  );
};

export const CustomerIdEditor = () => {
  const [filter, setFilter] = useCustomerIdAtom();
  const orderQueriesLoading = useOrderQueriesLoading();

  return (
    <TextInput
      id="customerId"
      placeholder="Filter value..."
      value={filter || ""}
      onChange={(e) => setFilter(e.currentTarget.value)}
      disabled={orderQueriesLoading}
    />
  );
};
